import React, { useEffect, useRef, useState } from "react";
import { MdClose, MdZoomIn } from "react-icons/md";

const DEFAULT_NOTE = "According to your measurement designs, then depends on the flare fabric estimation will vary. For further queries regarding customization, please feel free to call us at 9789389489. We are always ready to assist you. Thank you!";

const ZOOM = 2.5;

// Image that magnifies under the cursor. Desktop: hover to zoom, the zoom
// follows the pointer. Touch: tap to zoom in, drag to pan, tap again to reset.
const ZoomImage = ({ src, alt }) => {
  const boxRef = useRef(null);
  const [zoomed, setZoomed] = useState(false);
  const [origin, setOrigin] = useState({ x: 50, y: 50 });
  const isTouch = useRef(false);

  const moveTo = (clientX, clientY) => {
    const rect = boxRef.current.getBoundingClientRect();
    const x = Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100));
    const y = Math.min(100, Math.max(0, ((clientY - rect.top) / rect.height) * 100));
    setOrigin({ x, y });
  };

  return (
    <div
      ref={boxRef}
      className={`relative overflow-hidden rounded-xl border border-[var(--brand-primary-100)] bg-white shadow-sm ${
        zoomed ? "cursor-zoom-out" : "cursor-zoom-in"
      }`}
      style={{ touchAction: zoomed ? "none" : "auto" }}
      onMouseEnter={(e) => { if (!isTouch.current) { setZoomed(true); moveTo(e.clientX, e.clientY); } }}
      onMouseMove={(e) => { if (!isTouch.current) moveTo(e.clientX, e.clientY); }}
      onMouseLeave={() => { if (!isTouch.current) setZoomed(false); }}
      onTouchStart={(e) => {
        isTouch.current = true;
        const t = e.touches[0];
        moveTo(t.clientX, t.clientY);
      }}
      onTouchMove={(e) => {
        if (!zoomed) return;
        const t = e.touches[0];
        moveTo(t.clientX, t.clientY);
      }}
      onClick={() => { if (isTouch.current) setZoomed((z) => !z); }}
    >
      <img
        src={src}
        alt={alt}
        draggable={false}
        className="block w-full h-auto select-none transition-transform duration-200 ease-out"
        style={{
          transform: zoomed ? `scale(${ZOOM})` : "scale(1)",
          transformOrigin: `${origin.x}% ${origin.y}%`,
        }}
      />

      {!zoomed && (
        <span className="pointer-events-none absolute bottom-3 right-3 inline-flex items-center gap-1 rounded-full bg-black/60 px-3 py-1 text-[11px] font-medium text-white backdrop-blur-sm">
          <MdZoomIn size={14} />
          <span className="hidden sm:inline">Hover to zoom</span>
          <span className="sm:hidden">Tap to zoom</span>
        </span>
      )}
    </div>
  );
};

// Text and images are editable in Admin → HomePage → Product Page Tabs.
// `noteBold` (e.g. the phone number) is shown in bold inside the note.
const EstimatorModal = ({ isOpen, onClose, image1, image2, title = "Fabric Estimator", note = DEFAULT_NOTE, noteBold = "9789389489" }) => {
  // Esc to close + lock page scroll behind the modal.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const images = image2 ? [image1, image2] : [image1];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-3 sm:p-6"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="relative flex w-full max-w-5xl max-h-[92vh] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between gap-4 px-5 sm:px-6 py-4 flex-shrink-0"
          style={{ background: "linear-gradient(135deg, var(--brand-primary-900), var(--brand-primary-600))" }}
        >
          <div className="min-w-0">
            <h2 className="text-lg sm:text-xl font-bold text-white leading-tight truncate">{title}</h2>
            <p className="text-xs text-[var(--brand-primary-200)] mt-0.5">
              Check your size and how much fabric you need
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/30 transition"
          >
            <MdClose size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto p-4 sm:p-6" style={{ background: "var(--brand-primary-50)" }}>
          <div className={`grid gap-4 ${images.length > 1 ? "sm:grid-cols-2" : "grid-cols-1"}`}>
            {images.map((src, i) => (
              <ZoomImage key={i} src={src} alt={`${title} ${images.length > 1 ? i + 1 : ""}`.trim()} />
            ))}
          </div>

          <div className="mt-5 rounded-xl border border-[var(--brand-primary-100)] border-l-4 border-l-[var(--brand-primary-500)] bg-white p-4 text-sm leading-relaxed text-gray-700">
            <span className="font-semibold text-[var(--brand-primary-700)]">Note:</span>{" "}
            {(() => {
              const at = noteBold ? note.indexOf(noteBold) : -1;
              if (at < 0) return note;
              return (
                <>
                  {note.slice(0, at)}
                  <a href={`tel:${noteBold.replace(/\s/g, "")}`} className="font-semibold text-gray-900 underline-offset-2 hover:underline">
                    {noteBold}
                  </a>
                  {note.slice(at + noteBold.length)}
                </>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EstimatorModal;
